import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Row,
  Col,
  Image,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import blink from "../../assets/images/blink.png";
import { Culture } from "../../types/culture";
import { ListResponse, Pagination } from "../../types";
import cultureService from "../../services/cultureService";
import dayjs from "dayjs";
import CultureModal from "./CultureModal";

const { Text } = Typography;

const CulturesManagement = () => {
  const [form] = Form.useForm();

  // Culture states
  const [isCultureModalVisible, setIsCultureModalVisible] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Culture | null>(null);
  const [previewContent, setPreviewContent] = useState("");
  const [culturesData, setCulturesData] = useState<ListResponse<Culture>>();
  const [culturesPagination, setCulturesPagination] = useState<Pagination>({
    page: 1,
    resultsPerPage: 10,
  });
  const [isLoadingCultures, setIsLoadingCultures] = useState<boolean>(false);

  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);

  // Culture functions
  const getCultures = useCallback(async () => {
    try {
      setIsLoadingCultures(true);
      const response = await cultureService.getCultureList(culturesPagination);
      setCulturesData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách văn hoá: " + error);
    } finally {
      setIsLoadingCultures(false);
    }
  }, [culturesPagination]);

  useEffect(() => {
    getCultures();
  }, [getCultures]);

  const handleCulturesPaginationChange = (page: number, pageSize: number) => {
    setCulturesPagination({
      ...culturesPagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  const handleCulturesSearch = (value: string) => {
    setCulturesPagination({
      ...culturesPagination,
      page: 1,
      search: value,
    });
  };

  const showCultureModal = async (record: Culture | null = null) => {
    setIsCultureModalVisible(true);
    setIsModalLoading(true);
    try {
      if (record) {
        const culture = await cultureService.getCultureById(record.id);
        form.setFieldsValue(culture);
        setEditingRecord(culture);
      } else {
        form.resetFields();
        setEditingRecord(null);
      }
    } catch (error) {
      message.error("Lỗi khi lấy thông tin văn hoá: " + error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleCancelCultureModal = () => {
    setIsCultureModalVisible(false);
    form.resetFields();
    setEditingRecord(null);
  };

  const handlePreview = async (record: Culture) => {
    try {
      const response = await cultureService.getCultureById(record.id);
      setPreviewContent(response.content);
      setIsPreviewVisible(true);
    } catch (error) {
      message.error("Lỗi khi xem văn hoá: " + error);
    }
  };

  const handleDelete = (record: Culture) => {
    setEditingRecord(record);
    setIsDeleteVisible(true);
  };

  const confirmDelete = async () => {
    if (!editingRecord) return;
    try {
      await cultureService.deleteCulture(editingRecord.id);
      message.success(`Xoá văn hoá ${editingRecord.title} thành công`);
      await getCultures();
    } catch (error) {
      message.error("Lỗi khi xoá văn hoá: " + error);
    } finally {
      setEditingRecord(null);
      setIsDeleteVisible(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingRecord) {
        await cultureService.updateCulture(editingRecord.id, values);
        message.success(`Cập nhật văn hoá ${editingRecord.title} thành công`);
      } else {
        await cultureService.createCulture(values);
        message.success(`Thêm văn hoá ${values.title} thành công`);
      }
      handleCancelCultureModal();
      await getCultures();
    } catch (error) {
      message.error("Lỗi khi thêm/cập nhật văn hoá: " + error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const cultureColumns = [
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Tình trạng",
      dataIndex: "published",
      key: "published",
      render: (published: boolean) => {
        if (published)
          return (
            <Text style={{ color: "#52c41a", fontWeight: 500 }}>Hiện</Text>
          );
        return <Text style={{ color: "#faad14", fontWeight: 500 }}>Ẩn</Text>;
      },
    },
    {
      title: "Ngày đăng",
      dataIndex: "createdAt",
      key: "createdAt",
      render:(createdAt: string) => dayjs(createdAt).format('DD-MM-YYYY'),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: Culture) => (
        <Space>
          <Button
            icon={<EyeOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => handlePreview(record)}
          />
          <Button
            icon={<EditOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => showCultureModal(record)}
          />
          <Button
            icon={<DeleteOutlined style={{ paddingLeft: "4px" }} />}
            danger
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <h1 className="page-title">
        Quản lý văn hoá <Image src={blink} preview={false} className="blink-icon" />
      </h1>
      <Row
        style={{
          marginBottom: 16,
        }}
      >
        <Col
          xs={24}
          sm={15}
          md={12}
          lg={20}
          xl={20}
          style={{ marginTop: 10 }}
        >
          <Input.Search
            placeholder="Tìm kiếm"
            onChange={(e) => handleCulturesSearch(e.target.value)}
            style={{ maxWidth: 500 }}
            className="culture-search-input"
          />
        </Col>
        <Col
          xs={24}
          sm={9}
          md={12}
          lg={4}
          xl={4}
          style={{ textAlign: "right", marginTop: 10 }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showCultureModal()}
          >
            Thêm
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={cultureColumns}
        dataSource={culturesData?.data || []}
        pagination={{
          current: culturesPagination.page,
          pageSize: culturesPagination.resultsPerPage,
          total: culturesData?.totalResults || 0,
          onChange: handleCulturesPaginationChange,
        }}
        loading={isLoadingCultures}
        scroll={{ x: "max-content" }}
      />

      {/* Culture Modal */}
      <CultureModal
        visible={isCultureModalVisible}
        form={form}
        onFinish={handleSubmit}
        onCancel={handleCancelCultureModal}
        editingRecord={editingRecord}
        isModalLoading={isModalLoading}
        setIsModalLoading={setIsModalLoading}
      />

      {/* Preview Modal */}
      <Modal
        title="Culture Post Preview"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <div
          className="preview-content"
          dangerouslySetInnerHTML={{ __html: previewContent }}
        />
      </Modal>

      {/* Culture Delete Modal */}
      <Modal
        title="Xoá văn hoá"
        open={isDeleteVisible}
        onOk={confirmDelete}
        onCancel={() => setIsDeleteVisible(false)}
        okText="Xoá"
        okButtonProps={{ danger: true }}
        cancelText="Hủy"
      >
        Bạn có chắc chắn muốn xoá văn hoá {editingRecord?.title} không?
      </Modal>
    </div>
  );
};

export default CulturesManagement;
